import { LocalizedLink } from "@/components/LocalizedLink";
import { Globe, Shield, Scale, Users, Calculator, FileText, Home, Briefcase, ArrowRight, Award, Zap, Languages } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import { useLanguage } from "@/hooks/useLanguage";
import SEO from "@/components/SEO";
import immigrationHero from "@/assets/immigration-hero.jpg";
import criminalDefenseHero from "@/assets/criminal-defense-hero.jpg";
import personalInjuryHero from "@/assets/personal-injury-hero.jpg";
import familyLawHero from "@/assets/family-law-hero.jpg";
import taxLawHero from "@/assets/tax-law-hero.jpg";
import probateEstateHero from "@/assets/probate-estate-hero.jpg";
import realEstateHero from "@/assets/real-estate-hero.jpg";
import businessLawHero from "@/assets/business-law-hero.jpg";

const PracticeAreas = () => {
  const { t } = useLanguage();
  
  const areas = [
    {
      icon: Globe,
      image: immigrationHero,
      title: t('practices.immigration'),
      description: "Comprehensive immigration legal services including asylum, naturalization, work permits, family-based immigration, deportation defense, and more.",
      href: "/practice-areas/immigration-services",
      services: ["Asylum", "Citizenship & Naturalization", "Family Immigration", "Work Visas", "Deportation Defense"],
    },
    {
      icon: Shield,
      image: criminalDefenseHero,
      title: t('practices.criminal'),
      description: "Aggressive criminal defense representation for assault, DWI, drug charges, sex crimes, theft, white-collar crimes, and expungement.",
      href: "/practice-areas/criminal-defense",
      services: ["DWI Defense", "Drug Charges", "Assault Cases", "Expungement", "White Collar Crimes"],
    },
    {
      icon: Scale,
      image: personalInjuryHero,
      title: t('practices.injury'),
      description: "Fighting for maximum compensation in auto accidents, truck accidents, industrial accidents, and medical malpractice cases.",
      href: "/practice-areas/personal-injury",
      services: ["Auto Accidents", "Truck Accidents", "Industrial Accidents", "Medical Malpractice", "Premises Liability"],
    },
    {
      icon: Users,
      image: familyLawHero,
      title: t('practices.family'),
      description: "Compassionate family law representation for divorce, child custody, child support, CPS defense, adoption, and court order modifications.",
      href: "/practice-areas/family-law",
      services: ["Divorce", "Child Custody", "Child Support", "CPS Defense", "Adoption"],
    },
    {
      icon: Calculator,
      image: taxLawHero,
      title: t('practices.tax'),
      description: "Expert tax litigation, audit representation, and personal income tax services to protect your financial interests.",
      href: "/practice-areas/tax-law",
      services: ["Tax Litigation", "Tax Audits", "Personal Income Tax", "IRS Negotiations", "Tax Resolution"],
    },
    {
      icon: FileText,
      image: probateEstateHero,
      title: t('practices.probate'),
      description: "Comprehensive estate planning and probate administration, including wills, trusts, guardianship, and probate matters.",
      href: "/practice-areas/probate-estate",
      services: ["Wills", "Living Trusts", "Probate Administration", "Guardianship", "Estate Planning"],
    },
    {
      icon: Home,
      image: realEstateHero,
      title: t('practices.realestate'),
      description: "Complete real estate legal services for residential and commercial transactions, closings, foreclosures, and property disputes.",
      href: "/practice-areas/real-estate-law",
      services: ["Property Purchase/Sale", "Title Issues", "Closings", "Foreclosure Defense", "Landlord-Tenant"],
    },
    {
      icon: Briefcase,
      image: businessLawHero,
      title: t('practices.business'),
      description: "Business formation, trademark registration, contracts, partnership agreements, and business transactions.",
      href: "/practice-areas/business-law",
      services: ["Business Formation", "Trademark Registration", "Contracts", "Partnership Agreements", "Debt Collection"],
    },
  ];

  return (
    <div className="min-h-screen">
      <SEO useMapping={false} />
      <Header />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-hero text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">{t('pa.title')}</h1>
            <p className="text-xl opacity-95">
              {t('pa.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Practice Areas Grid */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {areas.map((area, index) => (
              <Card key={index} className="hover:shadow-xl transition-shadow overflow-hidden">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={area.image} 
                    alt={area.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent" />
                  <div className="absolute bottom-4 left-4 flex items-center gap-3">
                    <div className="h-12 w-12 rounded-lg bg-primary backdrop-blur-sm flex items-center justify-center">
                      <area.icon className="h-6 w-6 text-primary-foreground" />
                    </div>
                    <CardTitle className="text-2xl text-primary drop-shadow-lg font-bold">{area.title}</CardTitle>
                  </div>
                </div>
                <CardHeader className="pb-4">
                  <CardDescription className="text-base">{area.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <h4 className="font-semibold mb-2 text-sm text-primary">{t('pa.keyservices')}</h4>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {area.services.map((service, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <span className="text-primary mt-1 font-bold">•</span>
                          <span className="text-primary font-medium">{service}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Button asChild variant="default" className="w-full">
                    <LocalizedLink to={area.href}>
                      {t('cta.learn')} <ArrowRight className="ml-2 h-4 w-4" />
                    </LocalizedLink>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">{t('pa.why.title')}</h2>
            <p className="text-xl text-muted-foreground mb-12">
              {t('pa.why.subtitle')}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="bg-card p-6 rounded-lg shadow-md hover:shadow-xl transition-all">
                <div className="h-16 w-16 rounded-full bg-gradient-gold-shine flex items-center justify-center mx-auto mb-4 shadow-gold">
                  <Award className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-bold text-lg mb-2">{t('pa.why.expertise')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('pa.why.expertise.desc')}
                </p>
              </div>
              <div className="bg-card p-6 rounded-lg shadow-md hover:shadow-xl transition-all">
                <div className="h-16 w-16 rounded-full bg-gradient-gold-shine flex items-center justify-center mx-auto mb-4 shadow-gold">
                  <Languages className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-bold text-lg mb-2">{t('pa.why.trilingual')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('pa.why.trilingual.desc')}
                </p>
              </div>
              <div className="bg-card p-6 rounded-lg shadow-md hover:shadow-xl transition-all">
                <div className="h-16 w-16 rounded-full bg-gradient-gold-shine flex items-center justify-center mx-auto mb-4 shadow-gold">
                  <Zap className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-bold text-lg mb-2">{t('pa.why.response')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('pa.why.response.desc')}
                </p>
              </div>
            </div>

            <Button asChild size="lg" variant="cta">
              <LocalizedLink to="/appointment">{t('cta.schedule')}</LocalizedLink>
            </Button>
          </div>
        </div>
      </section>

      <CTASection
        title={t('pa.cta.title')}
        description={t('pa.cta.desc')}
      />

      <Footer />
    </div>
  );
};

export default PracticeAreas;
